/**
 * Reads what the moderation actually has on disk after a round — the export
 * zip, or the two JSON files out of it — and hands the playback view a
 * recording plus, for a Bildrunde, the comic sheet as a local blob URL.
 */

import { parseRecording, RecordingError, type PlaybackRecording } from '../../shared/playback';
import { unzip } from './unzip';

export interface LoadedRecording {
  recording: PlaybackRecording;
  /** The file the user picked, so the clip page can print a real command. */
  fileName: string;
  /** Blob URL of the comic sheet, when the round was a Bildrunde. */
  comicUrl: string | null;
  /** Non-fatal remarks for the loader ("Bildaufgabe ohne Comicdatei"). */
  notes: string[];
}

const IMAGE_RE = /\.(jpe?g|png|webp)$/i;

export async function loadRecording(files: File[]): Promise<LoadedRecording> {
  if (files.length === 0) throw new RecordingError('Keine Datei ausgewählt.');

  const jsons: { name: string; data: unknown }[] = [];
  const images: { name: string; blob: Blob }[] = [];

  for (const file of files) {
    if (file.name.toLowerCase().endsWith('.zip') || file.type === 'application/zip') {
      for (const entry of await unzip(await file.arrayBuffer())) {
        if (entry.name.toLowerCase().endsWith('.json')) {
          jsons.push({ name: entry.name, data: parseJson(entry.name, new TextDecoder().decode(entry.bytes)) });
        } else if (IMAGE_RE.test(entry.name)) {
          images.push({ name: entry.name, blob: new Blob([entry.bytes as BlobPart]) });
        }
      }
      continue;
    }
    if (IMAGE_RE.test(file.name) || file.type.startsWith('image/')) {
      images.push({ name: file.name, blob: file });
      continue;
    }
    if (file.name.toLowerCase().endsWith('.json') || file.type === 'application/json') {
      jsons.push({ name: file.name, data: parseJson(file.name, await file.text()) });
      continue;
    }
    // .txt / LIESMICH.txt out of the zip: not an error, just not needed.
  }

  if (jsons.length === 0) {
    throw new RecordingError(
      'In der Auswahl steckt kein Export. Gebraucht wird das Runden-Zip oder die Datei „…-keystrokes.json“.',
    );
  }

  const recording = parseRecording(jsons);
  const fileName = files[0]?.name ?? 'export.zip';
  const notes: string[] = [];
  if (!recording.hasStoryLayer) {
    notes.push('Ohne „…-story.json“: Text und Uhren spielen, Joker und Strafen fehlen.');
  }
  if (recording.keystrokeCount === 0) {
    notes.push('Ohne „…-keystrokes.json“: der Text erscheint fertig, nicht Anschlag für Anschlag.');
  }

  let comicUrl: string | null = null;
  if (recording.kind === 'comic') {
    const sheet = images[0];
    if (sheet) comicUrl = URL.createObjectURL(sheet.blob);
    else notes.push('Bildrunde ohne Comicdatei — Panels bleiben leer. Das Comicbild mit auswählen.');
  }

  return { recording, fileName, comicUrl, notes };
}

function parseJson(name: string, text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    throw new RecordingError(`„${name}“ ist kein gültiges JSON.`);
  }
}
